import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [waiting, setWaiting] = useState(true);
  const { clear } = useCart();
  const navigate = useNavigate();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setWaiting(false);
      return;
    }
    let ticks = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setWaiting(false);
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      ticks += 1;
      if (ticks < 15) setTimeout(poll, 2000);
      else setWaiting(false);
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B101E] px-6 pt-20 text-[#F2ECDF]" data-testid="payment-success-page">
      <div className="w-full max-w-lg text-center">
        {waiting ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-gold" strokeWidth={1.4} />
            <h1 className="mt-8 font-serif text-4xl italic">Confirming your elegance…</h1>
            <p className="mt-4 text-sm uppercase tracking-[0.26em] text-[#F2ECDF]/50">One moment with the maison</p>
          </>
        ) : order?.payment_status === "paid" ? (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-gold/50 bg-gold/10"
            >
              <Check className="h-9 w-9 text-gold" strokeWidth={1.8} />
            </motion.div>
            <p className="mt-8 text-[0.68rem] font-bold uppercase tracking-[0.34em] text-gold" data-testid="order-confirmed-label">Order Confirmed</p>
            <h1 className="mt-4 font-serif text-5xl leading-tight">Your steps are <em className="text-gold">couture</em> now.</h1>
            <div className="mx-auto mt-10 max-w-sm border border-white/15 p-7 text-left" data-testid="order-summary">
              {order.items?.map((i) => (
                <div key={`${i.product_id}-${i.size || "m"}`} className="flex justify-between py-2 text-sm text-[#F2ECDF]/80">
                  <span>{i.name}{i.size ? ` · Size ${i.size}` : ""} × {i.quantity}</span>
                  <span className="font-serif">${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="mt-4 flex justify-between border-t border-white/15 pt-4">
                <span className="text-[0.65rem] font-bold uppercase tracking-[0.28em] text-[#F2ECDF]/50">Total</span>
                <span className="font-serif text-2xl text-gold" data-testid="order-total">${order.amount?.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={() => navigate("/shop")}
              data-testid="success-continue-shopping"
              className="mt-10 bg-gold px-10 py-4 text-[0.7rem] font-bold uppercase tracking-[0.28em] text-[#0B101E] transition-colors duration-300 hover:bg-[#F2ECDF]"
            >
              Continue Stepping
            </button>
          </motion.div>
        ) : (
          <>
            <h1 className="font-serif text-4xl italic">We could not confirm this payment yet.</h1>
            <p className="mt-5 text-[#F2ECDF]/60">If your card was charged, your order will appear shortly. Reference: {sessionId?.slice(0, 18)}…</p>
            <button onClick={() => navigate("/shop")} data-testid="success-back-to-shop" className="mt-10 border border-white/30 px-10 py-4 text-[0.7rem] font-bold uppercase tracking-[0.28em] transition-colors duration-300 hover:bg-[#F2ECDF] hover:text-[#0B101E]">
              Back to the Collection
            </button>
          </>
        )}
      </div>
    </div>
  );
}
